/**
 * Inspiration wall (H-2026-09-12-128 2a) — the create cold-start entry.
 * Renders the official style seed wall (one tile per style_registry row)
 * into the docked Inspiration tab; each tile is a recipe entry: click → the
 * image goes into the A source slot → the existing bundle/vision read fires
 * → its cards land in this same panel (which then covers the wall).
 *
 * The "+ Use your own image" secondary button re-triggers the EXISTING
 * upload entry (the shell's hidden #createFileInput → handleImageUpload);
 * it does not reimplement upload.
 */

import { store } from '../../core/store.js'
import { eventBus, EVENTS } from '../../core/event-bus.js'
import { setMode } from '../../core/router.js'
import { CONFIG } from '../../core/config.js'
import { apiRequest, REQUEST_TIMEOUT_MS } from '../../api/index.js'
import { wallItems } from './style-seeds.js'
import { escAttr, escHtml } from '../create/dialog/shell-scaffold.js'
import { createLogger } from '../../utils/logger.js'
const log = createLogger('create-ui.inspiration-wall')

// Rendered once per successful load; a failed fetch leaves the wall
// re-renderable on the next create activation (seed fallback still applies
// on success-with-empty-pool, so this only skips a repeat network call).
let _loaded = false

/**
 * Route a seed/community image into the A source slot through the EXISTING
 * pick chain: same funnel as ?injectImage (ADR-005 2.7.0) — an external URL
 * with no typed slot ref. The shell's IMAGE_SELECT handler shows it in the A
 * slot and fires the vision read (VISION_COMPLETE → cards + style grid
 * chain); generate materializes the external source lazily (SSRF gate).
 * @param {string} url
 */
export function startFromSeedImage(url) {
  if (!url) return
  // The A slot lives in create mode — a pick from a base tab while in design
  // mode hands off to create first, or the shell's IMAGE_SELECT handler
  // (mode-gated) would drop it.
  if (store.getState('mode') !== 'create') setMode('create')
  store.setState('sourceImage', { url, source: 'inspiration-wall' })
  eventBus.emit(EVENTS.IMAGE_SELECT, { url, id: null })
}

/**
 * Render (once) the seed wall into #inspirationWallCol. Called from the
 * create mode's activate(); safe to call on every activation.
 */
export async function initInspirationWall() {
  const col = document.getElementById('inspirationWallCol')
  if (!col || _loaded) return
  col.innerHTML = '<div class="iw-wall-status">Loading styles…</div>'
  let items
  try {
    const res = await apiRequest(`${CONFIG.FRONTEND_API}/api/customer/styles`, {
      timeoutMs: REQUEST_TIMEOUT_MS.DEFAULT,
    })
    items = wallItems(res?.data?.styles ?? [])
  } catch (err) {
    // Offline/unreachable: the seed fallback still yields a full wall.
    log.warn('style pool fetch failed, using seed fallback:', err?.message || err)
    items = wallItems([])
  }
  _loaded = true
  renderWall(col, items)
}

function renderWall(col, items) {
  col.innerHTML = `
    <div class="iw-wall">
      <button type="button" class="iw-wall-upload" id="iwWallUpload">
        <i class="ph ph-plus" aria-hidden="true"></i><span>Use your own image</span>
      </button>
      <div class="iw-wall-grid" role="group" aria-label="Official style gallery">
        ${items
          .map(
            (item) => `
          <button type="button" class="iw-wall-item" data-url="${escAttr(item.imageUrl)}"
                  title="Start from ${escAttr(item.label)}">
            <img src="${escAttr(item.imageUrl)}" alt="${escAttr(item.label)}" loading="lazy" />
            <span class="iw-wall-item-label">${escHtml(item.label)}</span>
          </button>`
          )
          .join('')}
      </div>
    </div>`

  // Secondary upload entry — reuses the shell's existing hidden file input
  // (bound once in bindShellOnceListeners → handleImageUpload). No new upload
  // logic here.
  col.querySelector('#iwWallUpload')?.addEventListener('click', () => {
    document.getElementById('createFileInput')?.click()
  })
  col.querySelectorAll('.iw-wall-item').forEach((el) => {
    el.addEventListener('click', () => startFromSeedImage(el.dataset.url))
  })
}
